import {
  Component,
  computed,
  input,
  output,
  signal,
  effect,
  ChangeDetectionStrategy,
  ContentChild,
  TemplateRef,
  Type,
} from '@angular/core';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SoSectionCardUI } from '../so-section-card/so-section-card.component';
import { SoBadgeColors, SoBadgeUI } from '../so-badge/so-badge.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SoSvgIconUI } from "../so-svg-icon/so-svg-icon.component";

export interface SoTab {
  title: string;
  route?: string;
  icon?: string;
  svgIcon?: string;
  componentIcon?: {
    class: Type<any>;
    inputs: Record<string, any>;
  };
  iconSize?: number;
  iconScaleX?: number;
  iconScaleY?: number;
  iconRotation?: number;
  disabled?: boolean;
  styleButton?: string;
  styleTextSpan?: string;
  showNotificationIcon?: boolean;
  showIconUploadding?: boolean;
  badge?: string | number;
  badgeColor?: string;
  activeColor?: string;
  iconColor?: string;
  borderColor?: string;
  tooltip?: string;
  shortLabel?: string;
  callback?: () => void;
}

interface SoTabExtended extends SoTab { fixedIconColor: boolean };

export type SoTabsDisplay = 'horizontal' | 'horizontal-full' | 'vertical' | 'sections' | 'steps' | 'pills' | 'underline' | 'rail';
export type SoTabsType = 'simple' | 'principal' | 'bordered' | 'card';

@Component({
  selector: 'so-tabs-ui',
  standalone: true,
  imports: [CommonModule, NgComponentOutlet, MatTooltipModule, RouterModule, SoSectionCardUI, SoBadgeUI, SoSvgIconUI],
  templateUrl: './so-tabs.component.html',
  styleUrl: './so-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoTabsUI {
  @ContentChild('tabContent') tabContent?: TemplateRef<unknown>;
  @ContentChild('tabHeader') tabHeaderTemplate?: TemplateRef<unknown>;

  // Modern signal-based inputs
  tabs = input<SoTabExtended[], SoTab[]>([], { transform: v => v.map(v => {
    const n = { ...v, fixedIconColor: false };
    if (!n.componentIcon)
      return n;
    const color: undefined | null | string = n.componentIcon.inputs['color'];
    const hasFixedColor: boolean = color !== undefined && color !== null && color.trim() !== '';
    n.fixedIconColor = hasFixedColor;
    if (!hasFixedColor)
      n.componentIcon.inputs['color'] = '--so-slate-600, #475569';
    return n;
    }),
  });
  activeIndex = input<number | null>(null);
  display = input<SoTabsDisplay>('horizontal');
  type = input<SoTabsType>('simple');
  disabled = input<boolean>(false);
  
  // Styling
  tabContentClass = input<string>('');
  contentCard = input<boolean>(true);
  showContent = input<boolean>(true);
  animated = input<boolean>(true);
  fullWidth = input<boolean>(false);
  
  // Navigation
  useRouter = input<boolean>(false);
  queryParams = input<Record<string, string>>({});
  
  // Tooltips
  tooltipDisabledText = input<string>('');
  
  // Step-specific
  showStepNumbers = input<boolean>(true);
  allowStepNavigation = input<boolean>(true);
  
  // Section-specific
  sectionBorderColor = input<string>('--so-primary');
  
  // Backward compatibility inputs (from SoTabsComponent)
  tabsDisplay = input<SoTabsDisplay>('horizontal');
  activeTabIndex = input<number | null>(null);
  
  // Placeholder for sections
  placeholderText = input<string>('Selecciona una sección');
  placeholderIcon = input<string>('touch_app');

  // Outputs
  tabChanged = output<number>();
  activeTabIndexChange = output<number>();
  tabClicked = output<{ tab: SoTab; index: number }>();

  // Internal state
  protected currentIndex = signal<number | null>(null);

  constructor() {
    // Sync with input values
    effect(() => {
      const index = this.activeIndex() ?? this.activeTabIndex();
      this.currentIndex.set(index);
    }, {  });
  }
  
  // Check if no tab is selected
  protected hasSelection = computed(() => this.currentIndex() !== null);

  // Computed values
  protected effectiveDisplay = computed(() => this.display() || this.tabsDisplay() || 'horizontal');
  
  protected effectiveDisabled = computed(() => this.disabled());

  protected containerClasses = computed(() => ({
    'so-tabs': true,
    [`so-tabs--${this.effectiveDisplay()}`]: true,
    [`so-tabs--${this.type()}`]: true,
    'so-tabs--animated': this.animated(),
    'so-tabs--full-width': this.fullWidth(),
    'so-tabs--disabled': this.effectiveDisabled()
  }));

  protected navClasses = computed(() => ({
    'so-tabs__nav': true,
    [`so-tabs__nav--${this.effectiveDisplay()}`]: true
  }));

  protected contentClasses = computed(() => {
    const classes: Record<string, boolean> = {
      'so-tabs__content': true,
      'so-tabs__content--card': this.contentCard()
    };
    
    if (this.tabContentClass()) {
      classes[this.tabContentClass()] = true;
    }
    
    return classes;
  });

  protected indicatorStyle = computed(() => {
    const display = this.effectiveDisplay();
    const tabCount = this.tabs().length;
    const index = this.currentIndex();
    
    if (display !== 'horizontal' && display !== 'underline') return {};
    if (tabCount === 0 || index === null) return {};

    const width = 100 / tabCount;
    return {
      'width': `${width}%`,
      'transform': `translateX(${index * 100}%)`
    };
  });

  protected isPillsDisplay = computed(() => this.effectiveDisplay() === 'pills');

  protected isRailDisplay = computed(() => this.effectiveDisplay() === 'rail');

  // Methods
  setActiveTab(index: number): void {
    const tab = this.tabs()[index];
    if (tab?.disabled || this.effectiveDisabled()) return;

    this.updateComponentIconsColors(tab);
    this.currentIndex.set(index);
    this.tabChanged.emit(index);
    this.activeTabIndexChange.emit(index);
    this.tabClicked.emit({ tab, index });
  }

  protected updateComponentIconsColors(tab: SoTab) {
    this.tabs().forEach(t => {
      if (!t.componentIcon || t.fixedIconColor)
        return;
      if (t === tab)
        t.componentIcon.inputs['color'] = '--so-secondary';
      else
        t.componentIcon.inputs['color'] = '--so-slate-600, #475569';
    });
  }

  isActive(index: number): boolean {
    return this.currentIndex() === index;
  }
  
  protected showPlaceholder = computed(() => {
    return this.effectiveDisplay() === 'sections' && this.currentIndex() === null;
  });

  isTabDisabled(tab: SoTab): boolean {
    return tab.disabled || this.effectiveDisabled();
  }

  getTabClasses(tab: SoTab, index: number): Record<string, boolean> {
    return {
      'so-tabs__tab': true,
      'so-tabs__tab--active': this.isActive(index),
      'so-tabs__tab--disabled': this.isTabDisabled(tab),
      [`so-tabs__tab--${this.type()}`]: true
    };
  }

  getStepClasses(tab: SoTab, index: number): Record<string, boolean> {
    const current = this.currentIndex() ?? -1;
    return {
      'so-tabs__step': true,
      'so-tabs__step--active': index === current,
      'so-tabs__step--completed': current !== -1 && index < current,
      'so-tabs__step--disabled': this.isTabDisabled(tab),
      'so-tabs__step--upcoming': current === -1 || index > current
    };
  }

  getBadgeColor(tab: SoTab, index: number): string {
    if (tab.badgeColor) return tab.badgeColor;
    return this.isActive(index) ? 'var(--so-white)' : 'var(--so-tabs-active-color, var(--so-secondary))';
  }

  getPillBadgeColors(tab: SoTab, index: number): SoBadgeColors {
    const active = this.isActive(index);
    const base = tab.badgeColor || 'var(--so-pill-color, var(--so-secondary, #a3bd36))';

    if (active) {
      return {
        background: 'var(--so-white, #ffffff)',
        text: base
      };
    }

    return {
      background: `color-mix(in srgb, ${base} 18%, var(--so-white, #ffffff))`,
      text: base
    };
  }

  getRailLabel(tab: SoTab): string {
    return tab.shortLabel || tab.title;
  }

  getRailItemClasses(tab: SoTab, index: number): Record<string, boolean> {
    return {
      'so-tabs__rail-item': true,
      'so-tabs__rail-item--active': this.isActive(index),
      'so-tabs__rail-item--disabled': this.isTabDisabled(tab)
    };
  }

  trackByTab(index: number, tab: SoTab): string {
    return tab.route || tab.title;
  }
}
